import {useAppDispatch} from "store";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {OptionValue} from "model/form";
import {fetchContent, fetchContents} from "store/content";
import {SelectFormField} from "components/form/SelectFormField";
import {useFormikContext} from "formik";
import {get} from "react-hook-form";
import type {ContentInCompanySpaceIndexResponse} from "generated-api";

export const createContentOption = (c: Partial<ContentInCompanySpaceIndexResponse>): OptionValue => ({
    value: c.active_version!.content_id!,
    label: c.name!,
    params: {content: c}
});

export const ContentSelectFormField = ({name, label, placeholder, filterRole, initialContents, shrink}: {
    name: string,
    label: string,
    placeholder?: string,
    filterRole?: string,
    initialContents?: ContentInCompanySpaceIndexResponse[],
    shrink?: boolean,
}) => {
    const dispatch = useAppDispatch();

    const {initialValues, values} = useFormikContext();
    const valueIds = get(values as any, name) as number[] | undefined;
    const initialIds = get(initialValues as any, name) as number[] | undefined;

    const [isContentLoading, setIsContentLoading] = useState(false);
    const [contentSearch, setContentSearch] = useState('');
    const [contentOptions, setContentOptions] = useState<OptionValue[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    const knownContents = useRef<OptionValue[]>(initialContents?.filter(c => !!c.active_version?.content_id).map(createContentOption) || []);
    const lastSearch = useRef<string>();

    const loadInitialContentOptions = useCallback(async () => {
        if (!initialIds?.length) {
            return;
        }
        const missingIds = initialIds.filter((id) => !knownContents.current.find((ku) => ku.value === id));
        if (!missingIds.length) {
            return;
        }

        // manually fetch any contents initially selected and not returned by the opening search
        const contents: OptionValue[] = [];
        for (const missingId of missingIds) {
            const res = await dispatch(fetchContent({id: String(missingId)}));
            const content = createContentOption(res.payload as ContentInCompanySpaceIndexResponse);
            if (!knownContents.current.find((ca) => content.value === ca.value)) {
                knownContents.current.push(content);
            }
            contents.push(content);
        }
        setContentOptions((current) => {
            const missing = contents.filter(u => !current.find(c => c.value === u.value));
            if (!missing.length) {
                return current;
            }
            return [...current, ...missing];
        })

    }, [initialIds, dispatch]);

    useEffect(() => {
        if (lastSearch.current === contentSearch) {
            // no-op
            return;
        }
        setIsContentLoading(true);
        dispatch(fetchContents({
            perPage: 50,
            sort: "name:asc",
            name: contentSearch || undefined,
        })).then((res) => {
            const items = res.payload as ContentInCompanySpaceIndexResponse[];
            const contents: OptionValue[] = items?.filter(c => !!c.active_version?.id)?.map(createContentOption);
            if (valueIds?.length) {
                // always add currently selected contents to the options for chips
                knownContents.current.forEach((ca) => {
                    if (valueIds.indexOf(+ca.value) >= 0 && !contents?.find((a) => a.value === ca.value)) {
                        contents?.unshift(ca);
                    }
                });
            }
            // add fetched contents to the local cache for future use (might be needed for the chips when next search returns something else)
            contents?.forEach(a => {
                if (!knownContents.current.find((ca) => a.value === ca.value)) {
                    knownContents.current.push(a);
                }
            });
            setContentOptions(contents);
        }).finally(() => {
            loadInitialContentOptions().finally(() => {
                setIsContentLoading(false);
                setIsInitialized(true);
                lastSearch.current = contentSearch;
            });
        });
    }, [valueIds, contentSearch, loadInitialContentOptions, filterRole, dispatch]);

    return <SelectFormField name={!isInitialized ? name + 'Temp' : name}
        label={label}
        placeholder={placeholder}
        isMulti={true}
        showTooltip
        options={contentOptions}
        onSearch={setContentSearch}
        shrink={shrink}
        isLoading={isContentLoading}
        isInitializing={!!valueIds?.length && !isInitialized}/>;
};
